// Small, pure label/badge/HTML-snippet helpers, plus the static reference
// data (order statuses, payment methods/statuses, wallet categories) they
// look values up in. All pure — data in, string out, no DOM, no app state
// (`stockBadge` takes an already-loaded item and calls the inventory
// service's stockMeta(), also pure).
//
// Extracted from public/index.html's inline script (Phase 2 modularization,
// slice 8). This is a DIFFERENT class of extraction than the services
// (pricing/inventory/wallet/orders): those had a *behavioral* correctness
// property (idempotency, reconciliation) worth protecting; these are
// deterministic HTML-string builders, so the risk profile here is "does the
// generated markup change," verified the same way as everywhere else in
// this refactor — byte-diffing the output against the original before
// deleting it (see REFACTOR_SECURITY_REPORT.md, Phase 2 section) — NOT by
// changing how any onclick handler is wired, which is untouched.
import { esc, fmtDate, safeImageUrl } from '../utils/format.js';
import { ic } from '../utils/icons.js';
import { stockMeta } from '../services/inventory.js';
import { productsLabel } from '../services/pricing.js';

export const STATUSES = [
  { k: 'nouvelle', l: 'Nouvelle', c: '#f3e1e8', t: '#7a1f3d' },
  { k: 'incomplete', l: 'Info incomplète', c: '#fdecec', t: '#b91c1c' },
  { k: 'a_confirmer', l: 'À confirmer', c: '#fef4e2', t: '#92400e' },
  { k: 'confirmee', l: 'Confirmée', c: '#e7f7ee', t: '#166534' },
  { k: 'prete', l: 'Prête', c: '#e6f6f8', t: '#0e7490' },
  { k: 'envoyee', l: 'Envoyée', c: '#e6edfb', t: '#1d4ed8' },
  { k: 'transit', l: 'En livraison', c: '#eaf4fd', t: '#0369a1' },
  { k: 'livree', l: 'Livrée', c: '#d6f5e0', t: '#157a3e' },
  { k: 'reportee', l: 'Reportée', c: '#fef7d6', t: '#854d0e' },
  { k: 'injoignable', l: 'Injoignable', c: '#f3e8fb', t: '#7e22ce' },
  { k: 'refusee', l: 'Refusée', c: '#fde3e8', t: '#be123c' },
  { k: 'retournee', l: 'Retournée', c: '#fdead6', t: '#c2410c' },
  { k: 'annulee', l: 'Annulée', c: '#eceaeb', t: '#4b4045' }
];
export const statusMap = Object.fromEntries(STATUSES.map(s => [s.k, s]));

export const PAYMENT_METHODS = [{ k: 'cod', l: 'Paiement au livreur (COD)' }, { k: 'bank_transfer', l: 'Virement bancaire' }, { k: 'cash', l: 'Espèces' }];
export const PAYMENT_STATUSES = [{ k: 'unpaid', l: 'Non payé' }, { k: 'pending', l: 'À recevoir' }, { k: 'received', l: 'Reçu' }, { k: 'refunded', l: 'Remboursé' }];
export const WALLET_CATEGORIES = [{ k: 'ads', l: 'Publicité' }, { k: 'products', l: 'Achat de produits' }, { k: 'packaging', l: 'Achat d’emballages' }, { k: 'delivery', l: 'Livraison' }, { k: 'fees', l: 'Frais bancaires' }, { k: 'adjustment', l: 'Ajustement de compte' }, { k: 'other', l: 'Autre' }];

export function paymentMethodLabel(k) {
  return (PAYMENT_METHODS.find(x => x.k === k) || {}).l || k || '—';
}

export function paymentStatusLabel(k) {
  return (PAYMENT_STATUSES.find(x => x.k === k) || {}).l || k || '—';
}

export function paymentPill(status) {
  const k = status || 'unpaid';
  return `<span class="payment-pill ${esc(k)}">${esc(paymentStatusLabel(k))}</span>`;
}

export function categoryLabel(k) {
  if (k === 'order') return 'Paiement commande';
  return (WALLET_CATEGORIES.find(x => x.k === k) || {}).l || k || '—';
}

export function periodLabel(range) {
  return fmtDate(range.from) + ' – ' + fmtDate(range.to);
}

export function kpi(l, v, i, cls) {
  return `<div class="kpi ${cls || ''}"><span class="kic">${ic(i, 26)}</span><div class="lab">${l}</div><div class="val">${v}</div></div>`;
}

export function productThumb(p) {
  const src = p && safeImageUrl(p.imageUrl);
  return src ? `<img class="product-thumb" src="${src}" alt="${esc(p.name || 'Produit')}">` : `<span class="product-thumb placeholder">${ic('image', 20)}</span>`;
}

export function stockBadge(item) {
  const sm = stockMeta(item);
  return `<span class="stock-level ${sm.key}">${sm.label}</span>`;
}

export function movementTypeLabel(type) {
  return type === 'set' ? 'Comptage' : type === 'exit' ? 'Sortie' : 'Entrée';
}

export function badge(k) {
  const s = statusMap[k] || { l: k, c: '#eee', t: '#333' };
  return `<span class="status" style="background:${s.c};color:${s.t}">${esc(s.l)}</span>`;
}

// Slice 11: moved alongside the other label helpers rather than into
// pages/dashboard.js because it's shared with viewOrders (still inline,
// not yet extracted) — extracting it here once means both call the same
// module function instead of the dashboard module reaching sideways into
// index.html's not-yet-extracted code.
export function productDisplayHtml(o, products) {
  const label = String((o && o.orderLabel) || '').trim();
  return label
    ? ('<b>' + esc(label) + '</b><br><span class="hint">' + esc(productsLabel(products, o)) + '</span>')
    : esc(productsLabel(products, o));
}
